import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import VoiceInput from '../components/interview/VoiceInput';
import EvaluationCard from '../components/interview/EvaluationCard';
import ReportCard from '../components/interview/ReportCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SearchSelect from '../components/ui/SearchSelect';
import { careerService, internshipService } from '../services/api';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import SkeletonLoader from '../components/ui/SkeletonLoader';

const STAGES = {
    SETUP: 'setup',
    INTERVIEWING: 'interviewing',
    EVALUATING: 'evaluating',
    REPORT: 'report',
};

function InterviewSimulatorPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const answerRef = useRef(null);
    const recognitionRef = useRef(null);
    const synthRef = useRef(window.speechSynthesis);

    const [stage, setStage] = useState(STAGES.SETUP);
    const [applications, setApplications] = useState([]);
    const [selectedAppId, setSelectedAppId] = useState('');
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [evaluations, setEvaluations] = useState([]);
    const [currentEvaluation, setCurrentEvaluation] = useState(null);
    const [report, setReport] = useState(null);
    const [role, setRole] = useState('');
    const [company, setCompany] = useState('');
    const [voiceMode, setVoiceMode] = useState(true);
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const language = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    const speechLocale = language === 'fr' ? 'fr-FR' : 'en-US';

    useEffect(() => {
        internshipService.list()
            .then(res => setApplications(res.data))
            .catch(() => {});
        if (!('SpeechRecognition' in window) && !('webkitSpeechRecognition' in window)) {
            setVoiceSupported(false);
            setVoiceMode(false);
        }
        return () => { stopListening(); stopSpeaking(); };
    }, []);

    const speak = (text) => new Promise((resolve) => {
        stopSpeaking();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;
        const voices = synthRef.current.getVoices();
        const preferred = voices.find(v => v.lang.toLowerCase().startsWith(speechLocale.toLowerCase().slice(0, 2)) && v.name.includes('Natural'))
            || voices.find(v => v.lang.toLowerCase().startsWith(speechLocale.toLowerCase().slice(0, 2)))
            || voices[0];
        if (preferred) utterance.voice = preferred;
        utterance.lang = speechLocale;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => { setIsSpeaking(false); resolve(); };
        utterance.onerror = () => { setIsSpeaking(false); resolve(); };
        synthRef.current.speak(utterance);
    });

    const stopSpeaking = () => { synthRef.current.cancel(); setIsSpeaking(false); };

    const startListening = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = speechLocale;
        recognition.onresult = (event) => {
            let final = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) final += event.results[i][0].transcript;
            }
            setAnswer(prev => prev + final);
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    };

    const stopListening = () => {
        if (recognitionRef.current) recognitionRef.current.stop();
        setIsListening(false);
    };

    const handleStart = async () => {
        if (!selectedAppId) { setError(t('interview.errors.select_application')); return; }
        setLoading(true);
        setError('');
        try {
            const response = await careerService.startInterview(selectedAppId);
            setQuestions(response.data.questions);
            setRole(response.data.role);
            setCompany(response.data.company);
            setStage(STAGES.INTERVIEWING);
            setCurrentIndex(0);
            if (voiceMode) {
                setTimeout(async () => {
                    if (language === 'fr') {
                        await speak(`Bienvenue pour votre entretien ${response.data.role} chez ${response.data.company}. Question 1. ${response.data.questions[0]?.question}`);
                    } else {
                        await speak(`Welcome to your interview for ${response.data.role} at ${response.data.company}. Question 1. ${response.data.questions[0]?.question}`);
                    }
                }, 500);
            }
        } catch (err) {
            setError(t('interview.errors.start_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitAnswer = async () => {
        const finalAnswer = answer.trim();
        if (!finalAnswer || finalAnswer.length < 10) { setError(t('interview.errors.answer_too_short')); return; }
        stopListening();
        stopSpeaking();
        setLoading(true);
        setError('');
        setStage(STAGES.EVALUATING);
        try {
            const currentQuestion = questions[currentIndex];
            const response = await careerService.evaluateAnswer({
                question: currentQuestion.question,
                answer: finalAnswer,
                role,
                question_type: currentQuestion.type,
            });
            const evaluation = {
                ...response.data.evaluation,
                question: currentQuestion.question,
                answer: finalAnswer,
                question_type: currentQuestion.type,
            };
            setCurrentEvaluation(evaluation);
            setEvaluations(prev => [...prev, evaluation]);
            if (voiceMode) {
                if (language === 'fr') {
                    await speak(`Votre score est de ${evaluation.score} sur 10. ${evaluation.feedback}`);
                } else {
                    await speak(`Your score is ${evaluation.score} out of 10. ${evaluation.feedback}`);
                }
            }
        } catch (err) {
            setError(t('interview.errors.evaluate_failed'));
            setStage(STAGES.INTERVIEWING);
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        const nextIndex = currentIndex + 1;
        if (nextIndex >= questions.length) {
            setLoading(true);
            setStage(STAGES.EVALUATING);
            try {
                const response = await careerService.getInterviewReport({
                    role, company, evaluations: [...evaluations],
                });
                setReport(response.data.report);
                setStage(STAGES.REPORT);
                if (voiceMode) {
                    if (language === 'fr') {
                        await speak(`Entretien terminé. Votre score global est ${response.data.report.overall_score} sur 10. Note ${response.data.report.grade}. ${response.data.report.summary}`);
                    } else {
                        await speak(`Interview complete! Your overall score is ${response.data.report.overall_score} out of 10. Grade ${response.data.report.grade}. ${response.data.report.summary}`);
                    }
                }
            } catch (err) {
                setError(t('interview.errors.report_failed'));
                setStage(STAGES.INTERVIEWING);
            } finally {
                setLoading(false);
            }
        } else {
            setCurrentIndex(nextIndex);
            setAnswer('');
            setCurrentEvaluation(null);
            setStage(STAGES.INTERVIEWING);
            if (voiceMode) {
                setTimeout(async () => {
                    await speak(`Question ${nextIndex + 1}. ${questions[nextIndex]?.question}`);
                }, 300);
            } else {
                setTimeout(() => answerRef.current?.focus(), 100);
            }
        }
    };

    const handleRestart = () => {
        stopListening();
        stopSpeaking();
        setStage(STAGES.SETUP);
        setQuestions([]);
        setCurrentIndex(0);
        setAnswer('');
        setEvaluations([]);
        setCurrentEvaluation(null);
        setReport(null);
        setSelectedAppId('');
        setError('');
    };

    const appOptions = applications.map(app => ({
        value: String(app.id),
        label: `${app.position} at ${app.company_name}`,
    }));

    // ── SETUP ─────────────────────────────────────────────────────────────────
    if (stage === STAGES.SETUP) return (
        <Layout>
            <div className="page-container">
                <h1>{t('interview.title')}</h1>
                <p className="subtitle">{t('interview.subtitle')}</p>

                <div className="card" style={{ maxWidth: '600px', margin: '2rem auto' }}>
                    <h2>{t('interview.start_title')}</h2>
                    {error && <div className="error-message">{error}</div>}

                    <div className="form-group">
                        <label>{t('interview.select_application')}</label>
                        {applications.length === 0 ? (
                            <small className="hint">{t('resume.no_apps')} <a href="/internships">{t('resume.add_one')}</a></small>
                        ) : (
                            <SearchSelect
                                options={appOptions}
                                value={selectedAppId}
                                onChange={setSelectedAppId}
                                placeholder={t('resume.search_select_application')}
                            />
                        )}
                    </div>

                    <div className="form-group">
                        <label>{t('interview.mode')}</label>
                        <div className="cv-choice">
                            <label className={`cv-option ${voiceMode ? 'selected' : ''}`}>
                                <input type="radio" name="interview_mode" checked={voiceMode}
                                    onChange={() => setVoiceMode(true)} disabled={!voiceSupported} />
                                <div>
                                    <strong>{t('interview.voice_mode')}</strong>
                                    <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                        {t('interview.voice_mode_desc')}
                                        {!voiceSupported && ` ${t('interview.not_supported')}`}
                                    </p>
                                </div>
                            </label>
                            <label className={`cv-option ${!voiceMode ? 'selected' : ''}`}>
                                <input type="radio" name="interview_mode" checked={!voiceMode}
                                    onChange={() => setVoiceMode(false)} />
                                <div>
                                    <strong>{t('interview.text_mode')}</strong>
                                    <p style={{ fontSize: '0.8rem', color: '#64748b' }}>{t('interview.text_mode_desc')}</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="interview-info-box">
                        <div className="interview-info-item"><span>📝</span><span>{t('interview.info_1')}</span></div>
                        <div className="interview-info-item"><span>🤖</span><span>{t('interview.info_2')}</span></div>
                        <div className="interview-info-item"><span>📊</span><span>{t('interview.info_3')}</span></div>
                        {voiceMode && <div className="interview-info-item"><span>🔊</span><span>{t('interview.info_4')}</span></div>}
                    </div>

                    <button onClick={handleStart} disabled={loading || !selectedAppId}
                        className="btn-primary" style={{ width: '100%', marginTop: '1rem', padding: '0.85rem' }}>
                        {loading ? t('interview.preparing') : t('career.start_interview')}
                    </button>
                </div>
            </div>
        </Layout>
    );

    // ── EVALUATING LOADING ────────────────────────────────────────────────────
    if (stage === STAGES.EVALUATING && loading) return (
        <Layout>
            <div className="page-container">
                <LoadingSpinner message={
                    currentEvaluation === null && evaluations.length < questions.length
                        ? t('interview.evaluating')
                        : t('interview.generating_performance_report')
                } />
            </div>
        </Layout>
    );

    // ── INTERVIEWING ──────────────────────────────────────────────────────────
    if (stage === STAGES.INTERVIEWING) {
        const currentQuestion = questions[currentIndex];
        const progress = (currentIndex / questions.length) * 100;

        return (
            <Layout>
                <div className="page-container">
                    <div className="interview-header">
                        <div>
                            <h1>{t('interview.in_progress')}</h1>
                            <p className="subtitle">{role} at {company}</p>
                        </div>
                        <div className="interview-progress-info">
                            {t('interview.question_n_of_m', { n: currentIndex + 1, m: questions.length })}
                        </div>
                    </div>

                    <div className="interview-progress-bar">
                        <div className="interview-progress-fill" style={{ width: `${progress}%` }} />
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="card interview-card">
                        <div className="question-type-badge">
                            {currentQuestion?.type === 'technical' ? t('interview.technical') : t('interview.behavioral')}
                        </div>

                        {currentQuestion?.related_to && (
                            <p className="question-context">
                                {t('interview.from_job_description')}: {currentQuestion.related_to}
                            </p>
                        )}

                        <div className="question-row">
                            <h2 className="interview-question">{currentQuestion?.question}</h2>
                            {voiceMode && (
                                <button onClick={() => speak(currentQuestion?.question)}
                                    className="speak-btn" disabled={isSpeaking}>
                                    {isSpeaking ? '🔊' : '🔈'}
                                </button>
                            )}
                        </div>

                        {currentQuestion?.what_we_look_for && (
                            <p className="what-we-look-for">
                                {t('interview.tip')}: {currentQuestion.what_we_look_for}
                            </p>
                        )}

                        {voiceMode ? (
                            <>
                                <VoiceInput
                                    isListening={isListening}
                                    isSpeaking={isSpeaking}
                                    answer={answer}
                                    onStart={() => { setAnswer(''); startListening(); }}
                                    onStop={stopListening}
                                />
                                {answer && !isListening && (
                                    <button onClick={handleSubmitAnswer} disabled={loading}
                                        className="btn-primary"
                                        style={{ width: '100%', padding: '0.85rem', marginTop: '1rem' }}>
                                        {t('interview.submit_answer')} →
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                                    <label>{t('interview.your_answer')}</label>
                                    <textarea ref={answerRef} value={answer}
                                        onChange={e => setAnswer(e.target.value)}
                                        placeholder={t('interview.type_answer_here')}
                                        rows={8} style={{ resize: 'vertical' }} />
                                    <small className="hint">{answer.length} {t('interview.characters')}</small>
                                </div>
                                <button onClick={handleSubmitAnswer}
                                    disabled={loading || answer.trim().length < 10}
                                    className="btn-primary" style={{ width: '100%', padding: '0.85rem' }}>
                                    {loading ? t('interview.evaluating') : `${t('interview.submit_answer')} →`}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Layout>
        );
    }

    // ── EVALUATION RESULT ─────────────────────────────────────────────────────
    if (stage === STAGES.EVALUATING && currentEvaluation && !loading) {
        const scoreColor = currentEvaluation.score >= 8 ? '#10b981'
            : currentEvaluation.score >= 6 ? '#f59e0b' : '#ef4444';

        return (
            <Layout>
                <div className="page-container">
                    <div className="interview-header">
                        <div>
                            <h1>{t('interview.answer_feedback')}</h1>
                            <p className="subtitle">{t('interview.question_n_of_m', { n: currentIndex + 1, m: questions.length })}</p>
                        </div>
                        <div className="score-circle" style={{ borderColor: scoreColor }}>
                            <span style={{ color: scoreColor }}>{currentEvaluation.score}/10</span>
                        </div>
                    </div>
                    <EvaluationCard
                        evaluation={currentEvaluation}
                        currentIndex={currentIndex}
                        totalQuestions={questions.length}
                        onNext={handleNext}
                        loading={loading}
                    />
                </div>
            </Layout>
        );
    }

    // ── REPORT ────────────────────────────────────────────────────────────────
    if (stage === STAGES.REPORT && report) return (
        <Layout>
            <div className="page-container">
                <h1>{t('interview.complete')}</h1>
                <p className="subtitle">{role} at {company}</p>
                <ReportCard
                    report={report}
                    evaluations={evaluations}
                    role={role}
                    company={company}
                    onRestart={handleRestart}
                />
            </div>
        </Layout>
    );

    return null;
}

export default InterviewSimulatorPage;